import { useState, useEffect, useId } from 'react'
import { createPortal } from 'react-dom'

const STORAGE_KEY = 'gcc_show_tooltips'

export function useGccTooltipPreference(): [boolean, (v: boolean) => void] {
  const [show, setShow] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) !== 'false' } catch { return true }
  })
  const toggle = (v: boolean) => {
    try { localStorage.setItem(STORAGE_KEY, v ? 'true' : 'false') } catch { /* ignore */ }
    setShow(v)
  }
  return [show, toggle]
}

/** Enum legend rendered inside a tooltip — matches the ColTip/TipEnum pattern. */
export function TipEnum({ entries }: { entries: [string, string][] }) {
  return (
    <div className="mt-2 pt-2 border-t border-gray-600 space-y-1">
      {entries.map(([key, desc]) => (
        <div key={key}>
          <span className="font-semibold">{key}</span>
          <span className="text-gray-300"> — {desc}</span>
        </div>
      ))}
    </div>
  )
}

interface TooltipProps {
  content: React.ReactNode
  children?: React.ReactNode
  /** Hide the "?" icon from screen readers when the trigger already describes itself (aria-describedby) */
  iconHidden?: boolean
}

/** `left` is the tooltip's centre; `arrowOffset` keeps the arrow on the badge when the tooltip is clamped. */
interface TooltipPos { top: number; left: number; arrowOffset: number }

/** Half the tooltip's w-56 (224px) width, plus the gap kept from the window edge. */
const TIP_HALF_WIDTH = 112
const EDGE_GAP = 8

/** Centre the tooltip on the badge, but keep the whole tooltip inside the window horizontally. */
function clampTooltipLeft(badgeCentre: number, viewportWidth: number): number {
  const min = TIP_HALF_WIDTH + EDGE_GAP
  const max = viewportWidth - TIP_HALF_WIDTH - EDGE_GAP
  return Math.max(min, Math.min(badgeCentre, max))
}

export function GccTooltip({ content, children, iconHidden = false }: TooltipProps) {
  const [visible, setVisible] = useState(false)
  const [pos, setPos] = useState<TooltipPos | null>(null)
  const [show] = useGccTooltipPreference()
  const descriptionId = useId()

  useEffect(() => {
    if (!visible) return
    const handler = () => setVisible(false)
    document.addEventListener('scroll', handler, true)
    return () => document.removeEventListener('scroll', handler, true)
  }, [visible])

  /** Hover and keyboard focus both open the tooltip above the badge they fired on. */
  const open = (e: React.SyntheticEvent<HTMLSpanElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    const centre = r.left + r.width / 2
    const left = clampTooltipLeft(centre, window.innerWidth)
    setPos({ top: r.top, left, arrowOffset: centre - left })
    setVisible(true)
  }

  if (!show) return <>{children}</>

  return (
    <span className="relative inline-flex items-center gap-1">
      {children}
      <span
        className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[9px] font-bold cursor-help select-none flex-shrink-0"
        style={{ background: '#EDE9FE', color: '#6D28D9', lineHeight: 1 }}
        onMouseEnter={open}
        onMouseLeave={() => setVisible(false)}
        {...(iconHidden
          // The trigger is focusable and describes itself (WI 2576), so the icon stays out of the Tab order and the a11y tree
          ? { 'aria-hidden': true }
          : {
              onFocus: open,
              onBlur: () => setVisible(false),
              onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Escape') setVisible(false) },
              tabIndex: 0,
              'aria-label': 'More information',
              'aria-describedby': descriptionId,
            })}
      >
        ?
      </span>
      {/* Always in the DOM (WI 2577), so a screen reader hears the text on focus without hovering.
          Portalled like the tooltip, so the header's own text stays just its label. */}
      {!iconHidden && createPortal(<span id={descriptionId} className="sr-only">{content}</span>, document.body)}
      {/* Portalled to <body>: inside a <th> the tooltip inherited white-space: nowrap and
          text-align: right, so copy ran past the dark box as white-on-white and looked cut off. */}
      {visible && pos && createPortal(
        <span
          role="tooltip"
          style={{
            position: 'fixed',
            top: pos.top - 8,
            left: pos.left,
            transform: 'translate(-50%, -100%)',
            zIndex: 9999,
          }}
          className="block w-56 rounded-lg bg-gray-900 text-white text-left whitespace-normal break-words text-[11px] leading-relaxed px-3 py-2.5 shadow-xl pointer-events-none normal-case tracking-normal font-normal"
        >
          {content}
          <span
            data-testid="gcc-tooltip-arrow"
            style={{ position: 'absolute', top: '100%', left: `calc(50% + ${pos.arrowOffset}px)`, transform: 'translateX(-50%)', borderWidth: 4, borderStyle: 'solid', borderColor: 'transparent', borderTopColor: '#111827' }}
          />
        </span>,
        document.body,
      )}
    </span>
  )
}
